import React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Button,
	Dropdown as ReactBootstrapDropdown,
	Form,
	Modal,
	ModalProps,
	Tooltip,
	OverlayTrigger,
} from 'react-bootstrap';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';

import { AI_RESPONSE_SOURCE_IDS, individualResponseSourceOptions } from '@/lib/models';
import { accountService, AiPreferencesRequestBody } from '@/lib/services';
import { createUseThemedStyles, useAccount, useHandleError } from '@/hooks';
import { Dropdown, DropdownMenu } from '@/components';

const AiSettingsModalStyles = createUseStyles({
	dialog: {
		width: '90%',
		height: '100%',
		maxWidth: 620,
		margin: '0 auto',
		'& .modal-content': {
			maxHeight: '90vh',
		},
		'& .hackett-modal__body': {
			overflowY: 'auto',
		},
	},
});

export const AiSettingsModal = ({ ...props }: ModalProps) => {
	const classes = AiSettingsModalStyles();
	const { account, setAccount } = useAccount();
	const handleError = useHandleError();
	const [isSaving, setIsSaving] = useState(false);

	const allAiResponseSourceIds = useMemo(
		() => individualResponseSourceOptions.map((option) => option.aiResponseSourceId),
		[]
	);

	const availableAiResponseSourceIds = useMemo(
		() =>
			account?.availableAiResponseSourceIds && account.availableAiResponseSourceIds.length > 0
				? account.availableAiResponseSourceIds
				: allAiResponseSourceIds,
		[account?.availableAiResponseSourceIds, allAiResponseSourceIds]
	);

	const availableSourceOptions = useMemo(
		() =>
			individualResponseSourceOptions.filter((option) =>
				availableAiResponseSourceIds.includes(option.aiResponseSourceId)
			),
		[availableAiResponseSourceIds]
	);

	const selectedAiResponseSourceIds = useMemo(
		() => account?.aiPreferences?.aiResponseSourceIds ?? availableAiResponseSourceIds,
		[account?.aiPreferences?.aiResponseSourceIds, availableAiResponseSourceIds]
	);

	useEffect(() => {
		if (account?.aiPreferences) {
			return;
		}

		const setDefaultAiPreferences = async () => {
			setIsSaving(true);

			try {
				if (!account) {
					throw new Error('account is undfined.');
				}

				const response = await accountService
					.updateCurrentAccountAiPreferences(account.accountId, {
						shouldAnimate: true,
						showFollowUpQuestions: true,
						aiResponseSourceIds: availableAiResponseSourceIds,
					})
					.fetch();

				setAccount(response.account);
			} catch (error) {
				handleError(error);
			} finally {
				setIsSaving(false);
			}
		};

		setDefaultAiPreferences();
	}, [account, availableAiResponseSourceIds, handleError, setAccount]);

	const handleAiPreferenceChange = useCallback(
		async (data: Partial<AiPreferencesRequestBody>) => {
			setIsSaving(true);

			try {
				if (!account) {
					throw new Error('account is undfined.');
				}

				const response = await accountService
					.updateCurrentAccountAiPreferences(account.accountId, {
						shouldAnimate: account.aiPreferences?.shouldAnimate ?? true,
						showFollowUpQuestions: account.aiPreferences?.showFollowUpQuestions ?? true,
						aiResponseSourceIds: selectedAiResponseSourceIds,
						...data,
					})
					.fetch();
				setAccount(response.account);
			} catch (error) {
				handleError(error);
			} finally {
				setIsSaving(false);
			}
		},
		[account, handleError, selectedAiResponseSourceIds, setAccount]
	);

	return (
		<Modal dialogClassName={classes.dialog} centered {...props}>
			<Modal.Header closeButton>
				<Modal.Title>AskHackett&trade; Settings</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{account?.aiPreferences ? (
					<>
						<p className="mb-6">Choose which sources AskHackett&trade; should use by default.</p>
						<Form>
							<fieldset disabled={isSaving}>
								<div className="mb-5">
									<Form.Group>
										<Form.Label className="mb-0">Sources</Form.Label>
										<p className="mb-2 text-muted small">
											At least one IP source must be selected to generate LLM responses.
										</p>
										{availableSourceOptions.map((i) => (
											<React.Fragment key={i.aiResponseSourceId}>
												<Form.Check
													name="ai-response-source--modal"
													id={`ai-response-source--modal__${i.aiResponseSourceId}`}
													label={i.title}
													value={i.aiResponseSourceId}
													checked={selectedAiResponseSourceIds.includes(i.aiResponseSourceId)}
													onChange={() => {
														handleAiPreferenceChange({
															aiResponseSourceIds: selectedAiResponseSourceIds.includes(
																i.aiResponseSourceId
															)
																? selectedAiResponseSourceIds.filter(
																		(j) => j !== i.aiResponseSourceId
																  )
																: [
																		...selectedAiResponseSourceIds,
																		i.aiResponseSourceId,
																  ],
														});
													}}
													disabled={
														selectedAiResponseSourceIds.length === 1 &&
														selectedAiResponseSourceIds.includes(i.aiResponseSourceId)
													}
												/>
												<p className="mb-2 pl-5 text-muted small">{i.description}</p>
											</React.Fragment>
										))}
									</Form.Group>
								</div>
								<hr className="mb-5" />
								<Form.Group>
									<Form.Label className="mb-3">UI Preferences</Form.Label>
									<Form.Check
										name="show-follow-up-questions"
										id="show-follow-up-questions"
										label="Show recommended follow-up prompts"
										value="true"
										checked={account.aiPreferences.showFollowUpQuestions}
										onChange={({ currentTarget }) => {
											handleAiPreferenceChange({
												showFollowUpQuestions: currentTarget.checked,
											});
										}}
									/>
								</Form.Group>
							</fieldset>
						</Form>
					</>
				) : (
					<p className="mb-0 text-danger">Error loading ai preferences.</p>
				)}
			</Modal.Body>
			<Modal.Footer>
				<Button variant="outline-primary" onClick={props.onHide}>
					Close
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

const useAiSettingsDropdownStyles = createUseThemedStyles(
	(theme) => ({
		aiSettingsDropdownToggle: {
			display: 'flex',
			borderRadius: 5,
			padding: '7px 20px',
			alignItems: 'center',
			color: theme.colors.gray600,
			backgroundColor: theme.colors.gray100,
			border: `1px solid ${theme.colors.gray300}`,
			'&:after': {
				display: 'none !important',
			},
			'&:hover': {
				color: theme.colors.gray600,
				backgroundColor: theme.colors.gray200,
			},
			'&:active': {
				color: theme.colors.gray600,
				backgroundColor: theme.colors.gray300,
			},
		},
	}),
	{ index: 4 }
);

interface AiSettingsDropdownProps {
	aiResponseSourceIds: AI_RESPONSE_SOURCE_IDS[];
	onChange(aiResponseSourceIds: AI_RESPONSE_SOURCE_IDS[]): void;
	disabled?: boolean;
	sourceOptions?: typeof individualResponseSourceOptions;
}

export const AiSettingsDropdown = ({
	aiResponseSourceIds,
	onChange,
	disabled,
	sourceOptions = individualResponseSourceOptions,
}: AiSettingsDropdownProps) => {
	const classes = useAiSettingsDropdownStyles();

	return (
		<Dropdown autoClose="outside" drop="up">
			<ReactBootstrapDropdown.Toggle
				id="ai-settings-dropdown-toggle"
				className={classes.aiSettingsDropdownToggle}
				disabled={disabled}
			>
				<span className="mr-2">Sources:</span>
				{sourceOptions.map((i, index) => {
					const isLast = sourceOptions.length - 1 === index;
					return (
						<OverlayTrigger
							key={i.aiResponseSourceId}
							overlay={
								<Tooltip>
									<p className="mb-0 small">{i.title}</p>
								</Tooltip>
							}
						>
							<i.icon
								className={classNames({
									'mr-1': !isLast,
									'text-primary': aiResponseSourceIds.includes(i.aiResponseSourceId),
									'text-gray-300': !aiResponseSourceIds.includes(i.aiResponseSourceId),
								})}
								width={20}
								height={20}
							/>
						</OverlayTrigger>
					);
				})}
			</ReactBootstrapDropdown.Toggle>
			<DropdownMenu>
				<ReactBootstrapDropdown.Header>At least one source must be turned on</ReactBootstrapDropdown.Header>
				{sourceOptions.map((i) => (
					<ReactBootstrapDropdown.ItemText
						key={i.aiResponseSourceId}
						className="d-flex align-items-center justify-content-between"
					>
						<div className="d-flex align-items-center">
							<i.icon
								className={classNames('mr-3', {
									'text-primary': aiResponseSourceIds.includes(i.aiResponseSourceId),
									'text-gray-300': !aiResponseSourceIds.includes(i.aiResponseSourceId),
								})}
								width={24}
								height={24}
							/>
							<div>
								<p className="mb-0 font-weight-semi-bold small">{i.title}</p>
							</div>
						</div>
						<Form.Switch
							name="ai-response-source--dropdown"
							id={`ai-response-source--dropdown__${i.aiResponseSourceId}`}
							value={i.aiResponseSourceId}
							checked={aiResponseSourceIds.includes(i.aiResponseSourceId)}
							onChange={() => {
								onChange(
									aiResponseSourceIds.includes(i.aiResponseSourceId)
										? aiResponseSourceIds.filter((j) => j !== i.aiResponseSourceId)
										: [...aiResponseSourceIds, i.aiResponseSourceId]
								);
							}}
							label=""
							disabled={
								aiResponseSourceIds.length === 1 && aiResponseSourceIds.includes(i.aiResponseSourceId)
							}
						/>
					</ReactBootstrapDropdown.ItemText>
				))}
			</DropdownMenu>
		</Dropdown>
	);
};
